/* eslint-disable @typescript-eslint/no-explicit-any */
import { Col, Row } from '@design-system/components/grid/grid';
import { sprinkles } from '@design-system/sprinkles.css';

import { RadioSelect } from './components/RadioSelect';
import { TextInput } from './components/TextInput';
import { MenuSelect } from './components/MenuSelect';
import { Group } from './components/Group';
import { ToggleRow } from './components/ToggleRow';
import {
  Desc,
  Hint,
  Hint2,
  PriceRow,
  TitleRow,
} from './components/FormTypography';
import { DateInput } from './components/DateInput';
import { timezoneOptions } from '../../workout/components/Schedule';
import {
  DollarsInput,
  NumberInput,
  PercentInput,
} from './components/NumberInput';
import { InfoTip } from '../../Marketing/InfoTip';
import { wrapper } from './NewDiscount.view.css';
import {
  discountTypes,
  NewDiscountVM,
} from '../view-model/newDiscount.viewModel';
import { DiscountsVM } from '../view-model/discounts.viewModel';
import { TimeInput } from './components/TimeInput';
import { Errors } from './components/Errors';
import { AsyncRenderer } from '@client/new/components/AsyncRenderer/AsyncRenderer';
import { CircularProgress } from '@mui/material';
import { FormSelect } from './components/FormSelect';
import { Typography } from '@design-system/components/typography/typography';
import { ExternalLink } from '@design-system/components/link/externalLink';
import { space } from '@design-system/tokens';
import { HELP_LINKS } from '@client/new/common/helpLinks';

const freeTrialTooltip = (
  <Typography
    as="div"
    variant="body"
    size="small"
    color="N-20"
    style={{ padding: `${space.s} ${space.m}` }}
  >
    You can combine a Discount with your free trial.{' '}
    <ExternalLink
      href={HELP_LINKS.promotionsSetup}
      label="Click here"
      textVariant="span"
      textSize="small"
      textColor="N-20"
    />{' '}
    to read about when you&apos;d want to use this.
  </Typography>
);

type NewDiscountFormProps = NewDiscountVM & Pick<DiscountsVM, 'otps'>;

export const NewDiscountForm = ({
  register,
  onSubmit,
  values,
  ...p
}: NewDiscountFormProps) => (
  <form onSubmit={onSubmit}>
    <div className={wrapper} data-testid="NewDiscountForm">
      <TitleRow
        title="New Discount"
        button={{
          children: 'Create',
          color: 'neutral',
          onClick: onSubmit,
          loading: Boolean(p.inProgress),
        }}
      />
      <Errors data={p.errors} />
      <Col className={sprinkles({ gap: 'l' })}>
        <TextInput label="Name" {...register('name')} />
        <TextInput
          label="Discount code"
          {...register('discountCode')}
          ctaContent="Generate code"
          onClick={() => p.dispatch({ type: 'GenerateCode' })}
        />
        <Hint className={sprinkles({ paddingLeft: 'l' })}>
          The code your customers will enter during checkout. Uppercase letters
          and numbers only.
        </Hint>
      </Col>

      <InfoTip>
        {`Customers who use a coupon code won't be eligible for a free trial`}
      </InfoTip>

      <Group title="Products">
        <Desc>Select which product the discount applies to</Desc>
        <RadioSelect options={p.productTypes} {...register('salesType')} />
        {values.salesType === 'subscription' ? (
          <MenuSelect
            label="Subscription type"
            options={p.subTypes}
            {...(register('subscriptionType') as any)}
          />
        ) : (
          <FormSelect
            label="Discount applies to"
            options={p.otps}
            {...register('otpId')}
          />
        )}
      </Group>

      <Group title="Discount amount">
        <Desc>Select the type of discount you want to apply</Desc>
        <RadioSelect
          options={discountTypes}
          {...(register('discountType') as any)}
        />

        {values.discountType === 'FixedAmount' ? (
          <DollarsInput
            {...register('discountFixedValue')}
            width={140}
            min={0}
            max={1000000}
          />
        ) : values.discountType === 'Percentage' ? (
          <PercentInput
            {...register('discountPercentValue')}
            width={140}
            min={0}
            max={100}
          />
        ) : null}

        <AsyncRenderer
          data={p.prices}
          renderer={PriceRow}
          loadRender={<CircularProgress color="primary" />}
        />

        <InfoTip>
          <b>Note:</b> Discounts apply to the first billing cycle, i.e. first
          month for monthly subscription and first year for annual subscription.
        </InfoTip>
      </Group>

      <Group title="Settings">
        {values.salesType === 'subscription' &&
        p.isCouponWithFreeTrialEnabled ? (
          <ToggleRow
            title="Compatible with 7-day free trial"
            subTitle="Allow customers to use discount with 7-day free trial"
            tooltip={freeTrialTooltip}
            {...register('compatibleWithFreeTrial')}
          />
        ) : null}

        <ToggleRow
          title="Discount has an expiration date?"
          subTitle="Limit the date range when customers can redeem this discount"
          {...register('hasExpiration')}
        />

        {values.hasExpiration ? (
          <Col className={sprinkles({ gap: 'm' })}>
            <Desc>Redeem by</Desc>
            <Row style={{ alignItems: 'flex-start', marginTop: -2 }}>
              <DateInput {...register('expiration')} width={160} />
              <TimeInput register={register} values={values} width={160} />
              <MenuSelect
                {...(register('expirationTimezone') as any)}
                width={160}
                options={timezoneOptions}
              />
            </Row>
            <Row>
              <Hint2>{p.expirationDesc}</Hint2>
            </Row>
          </Col>
        ) : null}

        <ToggleRow
          title="Limit the number of redemptions?"
          subTitle="Limit the total number of discount can be redeemed"
          {...register('hasRedemptionsLimit')}
        >
          {values.hasRedemptionsLimit ? (
            <Col>
              <div style={{ maxWidth: 140 }}>
                <NumberInput
                  {...register('redemptionsLimit')}
                  min={0}
                  max={1000000}
                />
              </div>
            </Col>
          ) : null}
        </ToggleRow>
      </Group>
    </div>
  </form>
);
